import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, MessageSquare, Loader2 } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiService } from '../../services/api';
import { Visit, UpdateVisitRequest } from '../../types/visit';
import { formatUTCDate, formatUTCDateTime, formatDateForBackend, parseDateStringToLocal } from '../../utils/dateHelpers';

interface EditVisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  visit: Visit | null;
}

export const EditVisitModal: React.FC<EditVisitModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  visit
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [pendingComments, setPendingComments] = useState<string[]>([]);
  const [formData, setFormData] = useState<UpdateVisitRequest>({
    status: 'scheduled',
    visitType: 'initial',
    purpose: '',
    outcome: '',
    nextVisitRequired: false,
    nextVisitDate: '',
    tags: []
  });

  useEffect(() => {
    if (visit) {
      setFormData({
        status: visit.status,
        visitType: visit.visitType,
        purpose: visit.purpose || '',
        outcome: visit.outcome || '',
        nextVisitRequired: visit.nextVisitRequired || false,
        nextVisitDate: visit.nextVisitDate ? 
          (typeof visit.nextVisitDate === 'string' ? visit.nextVisitDate : formatDateForBackend(new Date(visit.nextVisitDate))) 
          : '',
        tags: visit.tags || []
      });
      // Reset pending comments when visit changes
      setPendingComments([]);
      setNewComment('');
      setShowCommentInput(false);
      // Clear error when modal opens
      setError('');
    }
  }, [visit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!visit) return;

    setError('');
    setLoading(true);

    try {
      // Prepare update data, excluding nextVisitDate if nextVisitRequired is false or if it's empty
      const updateData = { ...formData };
      if (!updateData.nextVisitRequired || !updateData.nextVisitDate) {
        delete updateData.nextVisitDate;
      }
      
      console.log('Sending update data:', updateData);
      
      // First update the visit
      await apiService.updateVisit(visit.id, updateData);
      
      // Then add all pending comments
      for (const comment of pendingComments) {
        await apiService.addVisitComment(visit.id, comment);
      }
      
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update visit');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = () => {
    if (!newComment.trim()) return;

    // Add comment to pending list instead of making API call
    setPendingComments(prev => [...prev, newComment]);
    setNewComment('');
    setShowCommentInput(false);
  };

  if (!visit) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Visit</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Provider Information (Read-only) */}
          <div className="space-y-4">
            <h3 className="font-semibold">Provider Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Provider NPI</Label>
                <Input value={visit.provider.npi} disabled />
              </div>
              <div>
                <Label>Provider Name</Label>
                <Input value={visit.provider.name} disabled />
              </div>
              <div>
                <Label>Specialty</Label>
                <Input value={visit.provider.specialty || ''} disabled />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={visit.provider.address || ''} disabled />
              </div>
            </div>
          </div>

          {/* Visit Details */}
          <div className="space-y-4">
            <h3 className="font-semibold">Visit Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Visit Date</Label>
                <Input 
                  value={formatUTCDate(visit.visitDate)} 
                  disabled 
                />
              </div>
              <div>
                <Label htmlFor="visitType">Visit Type</Label>
                <Select
                  value={formData.visitType}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, visitType: value as any }))}
                  disabled
                >
                  <SelectTrigger id="visitType" disabled>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="initial">Initial</SelectItem>
                    <SelectItem value="follow-up">Follow-up</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="status">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value as any }))}
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    {/* <SelectItem value="no-show">No Show</SelectItem> */}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="purpose">Visit Purpose</Label>
              <Textarea
                id="purpose"
                value={formData.purpose}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                rows={3}
                placeholder="Describe the purpose of this visit..."
              />
            </div>

            {formData.status === 'completed' && (
              <div>
                <Label htmlFor="outcome">Visit Outcome</Label>
                <Textarea
                  id="outcome"
                  value={formData.outcome || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, outcome: e.target.value }))}
                  rows={3}
                  placeholder="Describe the outcome of this visit..."
                />
              </div>
            )}
          </div>

          {/* Follow-up Information */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="nextVisitRequired"
                checked={formData.nextVisitRequired}
                onChange={(e) => setFormData(prev => ({ ...prev, nextVisitRequired: e.target.checked }))}
                className="h-4 w-4"
              />
              <Label htmlFor="nextVisitRequired" className="cursor-pointer">
                Follow-up visit required
              </Label>
            </div>

            {formData.nextVisitRequired && (
              <div>
                <Label htmlFor="nextVisitDate">Next Visit Date</Label>
                <DatePicker
                  selected={formData.nextVisitDate ? parseDateStringToLocal(formData.nextVisitDate) : null}
                  onChange={(date: Date | null) => setFormData(prev => ({ 
                    ...prev, 
                    nextVisitDate: date ? formatDateForBackend(date) : '' 
                  }))}
                  dateFormat="MMM d, yyyy"
                  minDate={new Date()}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md"
                  placeholderText="Select next visit date"
                />
              </div>
            )}
          </div>

          {/* Comments Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Comments</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCommentInput(true)}
                disabled={showCommentInput}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Comment
              </Button>
            </div>

            {showCommentInput && (
              <div className="space-y-2">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Enter your comment..."
                  rows={3}
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Add Comment
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowCommentInput(false);
                      setNewComment('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-2 max-h-48 overflow-y-auto">
              {/* Pending comments */}
              {pendingComments.length > 0 && (
                <>
                  <p className="text-xs text-muted-foreground font-medium">Pending comments (will be saved on update):</p>
                  {pendingComments.map((comment, index) => (
                    <Card key={`pending-${index}`} className="p-3 border-dashed border-yellow-500 bg-yellow-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm">{comment}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">
                              Pending
                            </span>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => setPendingComments(prev => prev.filter((_, i) => i !== index))}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </>
              )}
              
              {/* Existing comments */}
              {visit.comments && visit.comments.length > 0 ? (
                visit.comments.map((comment) => (
                  <Card key={comment.id} className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm">{comment.text}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">
                            {comment.user?.name || 'Unknown'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {comment.createdAt && formatUTCDateTime(comment.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))
              ) : (
                pendingComments.length === 0 && <p className="text-sm text-muted-foreground">No comments yet</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Visit
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
